import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useMutation } from '@tanstack/react-query';
import { requestHandler } from 'lib/api';
import { errorHandler } from 'lib/functions/errors';
import { Dispatch } from 'redux';

import { CreateBody, GetResponse, Transaction } from './types';

export const createTransaction = (dispatch: Dispatch, secretId: string) => {
  return useMutation({
    mutationKey: ['pruffConnect', 'transaction', 'create'],
    mutationFn: async (data: CreateBody): Promise<Transaction | undefined> => {
      const response = await requestHandler({
        method: 'post',
        url: `internal/express/${secretId}/transaction`,
        dispatch,
        data,
      });
      if (response) return response.data;
      return undefined;
    },
    onError: (error) => errorHandler(dispatch, error),
  });
};

export const commitTransaction = (dispatch: Dispatch, secretId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ['pruffConnect', 'transaction', 'commit'],
    mutationFn: async (id: string): Promise<Transaction | undefined> => {
      const response = await requestHandler({
        method: 'put',
        url: `internal/express/${secretId}/transaction/${id}/commit`,
        dispatch,
      });
      if (response) return response.data;
      return undefined;
    },
    onError: (error) => errorHandler(dispatch, error),
    onSuccess: async (_, id) => {
      await queryClient.invalidateQueries({
        queryKey: ['pruffConnect', 'transaction', 'get', id],
      });
    },
  });
};

export const getTransaction = (
  dispatch: Dispatch,
  secretId: string,
  id: string,
) => {
  return useQuery({
    queryKey: ['pruffConnect', 'transaction', 'get', id],
    queryFn: async (): Promise<GetResponse | undefined> => {
      const response = await requestHandler({
        method: 'get',
        url: `internal/express/${secretId}/transaction/${id}`,
        dispatch,
      });
      if (response) return response.data;
      return undefined;
    },
    enabled: !!id,
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 5,
    retry: 1,
  });
};
